import React, { MouseEvent, FC, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { toast } from "react-toastify";

import { toDateString } from "~/libs";

import {
  useApisManagersEmployeeConditionSurveysUpdate,
  useApisManagersEmployeeConditionSurveysShow,
  useBoolean,
  useDropdown,
  useInput,
  useMultiDivisionMultiSectionDropdown,
  useProvidersCurrentEmployee,
  useApisManagersEmployeeConditionSurveysNew,
} from "~/hooks";

import {
  EmployeeLayout,
  EmployeeLayoutMainContent,
  EmployeeLayoutRightSidebar,
} from "~/components/layouts";
import { AllDistributionCheckboxMultiParentsWithMultiChildrenDropdownField } from "~/components/organisms";

import { PublicStatus } from "~/domains/PublicStatus";
import {
  DatePicker,
  LabelWithDropDownField,
  LabelWithTextArea,
  LabelWithTextField,
  Loading,
} from "~/ui";
import { Button } from "~/ui";

export const ManagersEmployeeConditionSurveysEdit: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { currentEmployee } = useProvidersCurrentEmployee();
  const { mutate: updateRequest, isLoading: isSubmitting } =
    useApisManagersEmployeeConditionSurveysUpdate();
  const { data: surveyTemplates } =
    useApisManagersEmployeeConditionSurveysNew();
  const { data: surveyData, isLoading: isLoadingSurvey } =
    useApisManagersEmployeeConditionSurveysShow({ id: id! });
  const [{ value: title, onChange: onChangeTitle }, setTitle] = useInput("");
  const [
    { value: description, onChange: onChangeDescription },
    setDescription,
  ] = useInput("");
  const [dropdownAnswerShareOption] = useDropdown();
  const [dropdownSurveyTemplate] = useDropdown();
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const {
    isChecked: allBusinessSectionsDistribution,
    handleChange: onChangeAllBusinessSectionsDistribution,
  } = useBoolean(false);

  const {
    selectableDivisions,
    selectedDivisions,
    onDivisionChange,
    optionSelectableSections,
    optionSelectedSections,
    onSectionChange,
    setSelectedDivisions,
    setSelectedSections,
  } = useMultiDivisionMultiSectionDropdown({
    selectableDivisions: currentEmployee?.businessDivisions,
    selectableSections: currentEmployee?.businessSections,
  });

  useEffect(() => {
    if (surveyData && !isLoadingSurvey) {
      const survey = surveyData.employeeConditionSurvey;
      setTitle(survey.title);
      setDescription(survey.description);
      setStartDate(survey.startDate ? new Date(survey.startDate) : undefined);
      setEndDate(survey.endDate ? new Date(survey.endDate) : undefined);
      onChangeAllBusinessSectionsDistribution(
        survey.allBusinessSectionsDistribution,
      );

      if (survey.answerShareOption) {
        dropdownAnswerShareOption.onChange({
          value: survey.answerShareOption.id.toString(),
          label: survey.answerShareOption.name,
        });
      }

      if (survey.selectedSurveyTemplate) {
        dropdownSurveyTemplate.onChange({
          value: survey.selectedSurveyTemplate.id.toString(),
          label: survey.selectedSurveyTemplate.name,
        });
      }

      if (survey.businessSections) {
        setSelectedSections(survey.businessSections);
        const divisionIds = Array.from(
          new Set(
            survey.businessSections.map(
              (section) => section.businessDivisionId,
            ),
          ),
        );
        const divisions =
          currentEmployee?.businessDivisions?.filter((div) =>
            divisionIds.includes(div.id),
          ) || [];
        setSelectedDivisions(divisions);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    surveyData,
    isLoadingSurvey,
    currentEmployee,
    setTitle,
    setDescription,
    onChangeAllBusinessSectionsDistribution,
    setSelectedSections,
    setSelectedDivisions,
  ]);

  const breadCrumbItems = [
    { label: "コンディション", href: "/managers/employee_condition_surveys" },
    { label: "編集", href: `/managers/employee_condition_surveys/${id}/edit` },
  ];

  const handleSubmit =
    (publicStatusId: number) => (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      if (!id) return;

      const body = {
        title,
        description,
        startDate: startDate ? toDateString(startDate) : "",
        endDate: endDate ? toDateString(endDate) : "",
        answerShareOptionId: parseInt(
          dropdownAnswerShareOption.dropdownValue?.value || "1",
        ),
        allBusinessSectionsDistribution,
        businessSectionIds: optionSelectedSections.map((section) => section.id),
        publicStatusId,
      };

      updateRequest(
        { id, employeeConditionSurvey: body },
        {
          onSuccess: (data) => {
            toast(data.message);
            navigate("/managers/employee_condition_surveys");
          },
        },
      );
    };

  if (isLoadingSurvey) {
    return <Loading />;
  }
  console.log({ description });
  return (
    <EmployeeLayout withRightSidebar breadCrumbItems={breadCrumbItems}>
      <EmployeeLayoutMainContent withRightSidebar>
        <div className="space-y-6">
          <AllDistributionCheckboxMultiParentsWithMultiChildrenDropdownField
            allParents={selectableDivisions}
            parentsValue={selectedDivisions}
            parentsOnChange={onDivisionChange}
            parentRequired={true}
            parentLabel="配布先部署"
            allChildren={optionSelectableSections}
            childrenValue={optionSelectedSections}
            childrenOnChange={onSectionChange}
            childRequired={true}
            childLabel="配布先課"
            allBusinessSectionsDistribution={allBusinessSectionsDistribution}
            onChangeAllBusinessSectionsDistribution={
              onChangeAllBusinessSectionsDistribution
            }
            currentEmployeeRole={currentEmployee?.employeeRole}
          />
          <LabelWithTextField
            labelText="タイトル"
            type="text"
            name="title"
            placeholder="タイトル"
            required
            value={title}
            onChange={onChangeTitle}
          />
          <LabelWithTextArea
            labelText="説明"
            name="description"
            placeholder="説明"
            required
            value={description}
            onChange={onChangeDescription}
          />
          <LabelWithDropDownField
            labelText="質問テンプレート選択"
            required
            name="dropdownSurveyTemplate"
            options={surveyTemplates?.surveyTemplates.map((template) => ({
              value: template.id.toString(),
              label: template.name,
            }))}
            isDisabled
            value={dropdownSurveyTemplate.dropdownValue}
            onChange={dropdownSurveyTemplate.onChange}
          />
          <LabelWithDropDownField
            labelText="共有方法の選択"
            required
            name="dropdownAnswerShareOption"
            options={surveyTemplates?.answerShareOptions.map((status) => ({
              value: status.id.toString(),
              label: status.name,
            }))}
            value={dropdownAnswerShareOption.dropdownValue}
            onChange={dropdownAnswerShareOption.onChange}
          />
          <DatePicker
            labelText="開始日"
            selectDate={startDate}
            onChange={setStartDate}
            parentClassName="mt-6"
            withoutButton
            required
            textLeft
          />
          <DatePicker
            labelText="終了日"
            selectDate={endDate}
            onChange={setEndDate}
            parentClassName="mt-6"
            withoutButton
            textLeft
          />
          <div className="flex gap-4">
            <Button
              color="gray"
              className="flex-1"
              readonly={isSubmitting}
              onClick={handleSubmit(PublicStatus.DRAFTED.id)}
              text="下書き保存"
            />
            <Button
              color="primary"
              className="flex-1"
              readonly={isSubmitting}
              onClick={handleSubmit(PublicStatus.PUBLISHED.id)}
              text="配布する"
            />
          </div>
        </div>
      </EmployeeLayoutMainContent>
      <EmployeeLayoutRightSidebar />
    </EmployeeLayout>
  );
};
